import { computed, readonly, ref } from 'vue';

import { useCapacityCalendarStore } from '@/store/capacityCalendarStore';
import { isPastWeekKey } from '@/utils/calendarBuckets';
import type { ScheduleGroup } from '@/utils/scheduleBoard';

export interface ScheduleBoardDragPayload {
  group: ScheduleGroup;
  vendorCode: string;
  fromWeekKey: string;
}

export interface ScheduleBoardDropTargetCandidate {
  vendorCode: string;
  weekKey: string;
}

export interface ScheduleBoardDropResolution {
  kind: 'group' | 'assignment';
  group: ScheduleGroup;
  fromWeekKey: string;
  targetWeekKey: string;
}

export interface QueueDragPayload {
  poNumber: string;
  vendorCode: string;
}

export interface QueueDropResolution {
  kind: 'queue';
  poNumber: string;
  targetWeekKey: string;
}

// Module-scoped so every board cell instance shares one live drag state —
// mirrors useCalendarDragDrop's architecture, but this composable's payloads
// are group-anchored (a whole PO block) rather than type-row-anchored.
const activePayload = ref<ScheduleBoardDragPayload | null>(null);
// A queue card is not a block: it has no source week and no portions, so it gets its own slot
// rather than a nullable shape bolted onto the block payload.
const activeQueuePayload = ref<QueueDragPayload | null>(null);

function isCompatibleBlockTarget(candidate: ScheduleBoardDropTargetCandidate): boolean {
  const payload = activePayload.value;
  if (!payload) {
    return false;
  }

  if (candidate.vendorCode !== payload.vendorCode || candidate.weekKey === payload.fromWeekKey) {
    return false;
  }

  // Work already done cannot be planned differently, so a week that has ended is not a place
  // anything can be dropped. Dragging out of one and into the future stays allowed.
  if (isPastWeekKey(candidate.weekKey)) {
    return false;
  }

  const { allocations } = useCapacityCalendarStore();
  return payload.group.portions.every((portion) => Boolean(allocations[portion.rowId]?.[candidate.weekKey]));
}

function isCompatibleQueueTarget(candidate: ScheduleBoardDropTargetCandidate): boolean {
  const payload = activeQueuePayload.value;
  if (!payload || candidate.vendorCode !== payload.vendorCode || isPastWeekKey(candidate.weekKey)) {
    return false;
  }

  // Any planned capacity row for the vendor in that week is enough: the dialog decides types.
  const { rows, allocations } = useCapacityCalendarStore();
  return rows.some((row) => row.vendorCode === candidate.vendorCode && row.capacityTypeId && Boolean(allocations[row.id]?.[candidate.weekKey]));
}

function isCompatibleTarget(candidate: ScheduleBoardDropTargetCandidate): boolean {
  return activeQueuePayload.value ? isCompatibleQueueTarget(candidate) : isCompatibleBlockTarget(candidate);
}

export function useScheduleBoardDragDrop() {
  const isDragging = computed(() => activePayload.value !== null || activeQueuePayload.value !== null);

  function startDrag(payload: ScheduleBoardDragPayload): void {
    activeQueuePayload.value = null;
    activePayload.value = payload;
  }

  function startQueueDrag(payload: QueueDragPayload): void {
    activePayload.value = null;
    activeQueuePayload.value = payload;
  }

  function cancelDrag(): void {
    activePayload.value = null;
    activeQueuePayload.value = null;
  }

  function completeDrop(candidate: ScheduleBoardDropTargetCandidate): ScheduleBoardDropResolution | QueueDropResolution | null {
    if (activeQueuePayload.value) {
      if (!isCompatibleQueueTarget(candidate)) {
        return null;
      }

      const resolution: QueueDropResolution = { kind: 'queue', poNumber: activeQueuePayload.value.poNumber, targetWeekKey: candidate.weekKey };
      cancelDrag();

      return resolution;
    }

    const payload = activePayload.value;
    if (!payload || !isCompatibleTarget(candidate)) {
      return null;
    }

    const resolution: ScheduleBoardDropResolution = {
      kind: payload.group.portions.length > 1 ? 'group' : 'assignment',
      group: payload.group,
      fromWeekKey: payload.fromWeekKey,
      targetWeekKey: candidate.weekKey,
    };

    cancelDrag();

    return resolution;
  }

  return {
    activePayload: readonly(activePayload),
    isDragging,
    startDrag,
    startQueueDrag,
    cancelDrag,
    isCompatibleTarget,
    completeDrop,
  };
}
